import type { Translation } from 'primeng/api';

// PrimeNG translation maps applied via the PrimeNG config service (setTranslation)
// on init and on every language change. This localizes PrimeNG components that do
// not go through ngx-translate — most visibly the datepicker (month/day names,
// firstDayOfWeek) plus filter and a few general labels (clear/apply/today/empty
// messages). Kept in sync with the SupportedLanguage union in LanguageService.

const en: Translation = {
  startsWith: 'Starts with',
  contains: 'Contains',
  notContains: 'Not contains',
  endsWith: 'Ends with',
  equals: 'Equals',
  notEquals: 'Not equals',
  noFilter: 'No Filter',
  lt: 'Less than',
  lte: 'Less than or equal to',
  gt: 'Greater than',
  gte: 'Greater than or equal to',
  is: 'Is',
  isNot: 'Is not',
  before: 'Before',
  after: 'After',
  dateIs: 'Date is',
  dateIsNot: 'Date is not',
  dateBefore: 'Date is before',
  dateAfter: 'Date is after',
  clear: 'Clear',
  apply: 'Apply',
  matchAll: 'Match All',
  matchAny: 'Match Any',
  addRule: 'Add Rule',
  removeRule: 'Remove Rule',
  accept: 'Yes',
  reject: 'No',
  choose: 'Choose',
  upload: 'Upload',
  cancel: 'Cancel',
  pending: 'Pending',
  completed: 'Completed',
  fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  chooseYear: 'Choose Year',
  chooseMonth: 'Choose Month',
  chooseDate: 'Choose Date',
  prevDecade: 'Previous Decade',
  nextDecade: 'Next Decade',
  prevYear: 'Previous Year',
  nextYear: 'Next Year',
  prevMonth: 'Previous Month',
  nextMonth: 'Next Month',
  prevHour: 'Previous Hour',
  nextHour: 'Next Hour',
  prevMinute: 'Previous Minute',
  nextMinute: 'Next Minute',
  prevSecond: 'Previous Second',
  nextSecond: 'Next Second',
  am: 'am',
  pm: 'pm',
  dateFormat: 'mm/dd/yy',
  firstDayOfWeek: 0,
  today: 'Today',
  weekHeader: 'Wk',
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
  passwordPrompt: 'Enter a password',
  emptyMessage: 'No results found',
  emptyFilterMessage: 'No results found',
};

const it: Translation = {
  startsWith: 'Inizia con',
  contains: 'Contiene',
  notContains: 'Non contiene',
  endsWith: 'Finisce con',
  equals: 'Uguale a',
  notEquals: 'Diverso da',
  noFilter: 'Nessun filtro',
  lt: 'Minore di',
  lte: 'Minore o uguale a',
  gt: 'Maggiore di',
  gte: 'Maggiore o uguale a',
  is: 'È',
  isNot: 'Non è',
  before: 'Prima',
  after: 'Dopo',
  dateIs: 'La data è',
  dateIsNot: 'La data non è',
  dateBefore: 'La data è prima di',
  dateAfter: 'La data è dopo',
  clear: 'Cancella',
  apply: 'Applica',
  matchAll: 'Rispetta tutti',
  matchAny: 'Rispetta almeno uno',
  addRule: 'Aggiungi regola',
  removeRule: 'Rimuovi regola',
  accept: 'Sì',
  reject: 'No',
  choose: 'Scegli',
  upload: 'Carica',
  cancel: 'Annulla',
  pending: 'In attesa',
  completed: 'Completato',
  fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
  dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
  dayNamesMin: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
  monthNames: [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ],
  monthNamesShort: [
    'Gen',
    'Feb',
    'Mar',
    'Apr',
    'Mag',
    'Giu',
    'Lug',
    'Ago',
    'Set',
    'Ott',
    'Nov',
    'Dic',
  ],
  chooseYear: 'Scegli anno',
  chooseMonth: 'Scegli mese',
  chooseDate: 'Scegli data',
  prevDecade: 'Decennio precedente',
  nextDecade: 'Decennio successivo',
  prevYear: 'Anno precedente',
  nextYear: 'Anno successivo',
  prevMonth: 'Mese precedente',
  nextMonth: 'Mese successivo',
  prevHour: 'Ora precedente',
  nextHour: 'Ora successiva',
  prevMinute: 'Minuto precedente',
  nextMinute: 'Minuto successivo',
  prevSecond: 'Secondo precedente',
  nextSecond: 'Secondo successivo',
  am: 'am',
  pm: 'pm',
  dateFormat: 'dd/mm/yy',
  // In Italy the week starts on Monday.
  firstDayOfWeek: 1,
  today: 'Oggi',
  weekHeader: 'Set',
  weak: 'Debole',
  medium: 'Media',
  strong: 'Forte',
  passwordPrompt: 'Inserisci una password',
  emptyMessage: 'Nessun risultato',
  emptyFilterMessage: 'Nessun risultato',
};

export const primeNgTranslations: Record<'en' | 'it', Translation> = { en, it };
